// Fibery API Integration Module for Client Portal
// Handles visitor lookups and creation via the proxy server

import { createUniFiVisitor } from "./unifi";

const PROXY_URL = "/fibery";

// GraphQL query to find all visitors
const FIND_VISITOR_QUERY = `
  query FindVisitors {
    findVisitorsPeople {
      id
      firstName
      surname
      email
      phone
      company
      ndaSigned
    }
  }
`;

// GraphQL mutation to create a new visitor
const CREATE_VISITOR_MUTATION = `
  mutation CreateVisitor($firstName: String!, $surname: String!, $email: String, $phone: String) {
    visitorsPeople {
      create(firstName: $firstName, surname: $surname, email: $email, phone: $phone) {
        entities {
          id
          type
        }
      }
    }
  }
`;

// GraphQL query to get visitor details by ID
const GET_VISITOR_QUERY = `
  query GetVisitor($visitorId: ID!) {
    findVisitorsPeople(id: {is: $visitorId}) {
      id
      firstName
      surname
      email
      phone
      company
      ndaSigned
    }
  }
`;

// GraphQL mutation to create a new visit record
const CREATE_VISIT_MUTATION = `
  mutation CreateVisit($visitorId: ID!, $visitStart: String!, $visitEnd: String!) {
    visitorsVisits {
      create(visitorsPerson: {id: {is: $visitorId}}, visitStart: $visitStart, visitEnd: $visitEnd) {
        entities {
          id
          type
        }
      }
    }
  }
`;

// GraphQL query to get visit details by ID
const GET_VISIT_QUERY = `
  query GetVisit($visitId: ID!) {
    findVisitorsVisits(id: {is: $visitId}) {
      id
      visitStart
      visitEnd
      visitorsPerson {
        id
        firstName
        surname
      }
    }
  }
`;

// GraphQL mutation to update visitor NDA signed status
const UPDATE_NDA_SIGNED_MUTATION = `
  mutation UpdateNdaSigned($visitorId: ID!, $ndaSigned: Boolean!) {
    visitorsPeople(id: {is: $visitorId}) {
      update(ndaSigned: $ndaSigned) {
        message
      }
    }
  }
`;

/**
 * Make a GraphQL request to Fibery API
 * @param {string} query - The GraphQL query or mutation
 * @param {object} variables - Variables for the query
 * @returns {Promise<object>} The response data
 */
async function makeGraphQLRequest(query, variables = {}) {
    try {
        console.log(`Making GraphQL request to ${PROXY_URL}`);
        const response = await fetch(PROXY_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                query,
                variables,
            }),
        });

        console.log(`Response status: ${response.status}`);

        if (!response.ok) {
            const text = await response.text();
            console.error(`HTTP Error ${response.status}: ${text}`);
            throw new Error(`HTTP ${response.status}: ${text}`);
        }

        const text = await response.text();
        console.log(`Response text: ${text.substring(0, 200)}`);

        if (!text) {
            throw new Error("Empty response from proxy");
        }

        const data = JSON.parse(text);

        if (data.errors) {
            console.error("Fibery GraphQL Error:", data.errors);
            throw new Error(`Fibery API Error: ${data.errors[0].message}`);
        }

        return data.data;
    } catch (error) {
        console.error("Fibery API Request Error:", error);
        throw error;
    }
}

/**
 * Find an existing visitor by email
 * @param {string} email - The visitor's email
 * @returns {Promise<object|null>} Visitor object or null if not found
 */
async function findVisitorByEmail(email) {
    try {
        const result = await makeGraphQLRequest(FIND_VISITOR_QUERY, {});

        const visitors = result.findVisitorsPeople;
        if (visitors && Array.isArray(visitors) && visitors.length > 0) {
            // Filter by email since the query doesn't support filtering
            const matchingVisitor = visitors.find((v) => v.email === email);
            if (matchingVisitor) {
                return matchingVisitor;
            }
        }

        return null;
    } catch (error) {
        console.error("Error finding visitor:", error);
        throw error;
    }
}

/**
 * Find an existing visitor by first name and surname
 * @param {string} firstName - First name
 * @param {string} surname - Surname
 * @returns {Promise<object|null>} Visitor object or null if not found
 */
async function findVisitorByName(firstName, surname) {
    try {
        const result = await makeGraphQLRequest(FIND_VISITOR_QUERY, {});

        const visitors = result.findVisitorsPeople;
        if (visitors && Array.isArray(visitors) && visitors.length > 0) {
            // Filter by name since the query doesn't support filtering
            const matchingVisitor = visitors.find(
                (v) => v.firstName === firstName && v.surname === surname,
            );
            if (matchingVisitor) {
                return matchingVisitor;
            }
        }

        return null;
    } catch (error) {
        console.error("Error finding visitor by name:", error);
        throw error;
    }
}

/**
 * Create a new visitor in Fibery
 * @param {string} firstName - First name
 * @param {string} surname - Surname
 * @param {string} email - Email address
 * @param {string} phone - Phone number (optional)
 * @returns {Promise<object>} Created visitor object
 */
async function createVisitor(firstName, surname, email, phone = "") {
    try {
        const createResult = await makeGraphQLRequest(CREATE_VISITOR_MUTATION, {
            firstName,
            surname,
            email,
            phone: phone || null,
        });

        if (
            createResult.visitorsPeople &&
            createResult.visitorsPeople.create &&
            createResult.visitorsPeople.create.entities &&
            createResult.visitorsPeople.create.entities.length > 0
        ) {
            const createdId = createResult.visitorsPeople.create.entities[0].id;

            // Fetch the full visitor data
            const getResult = await makeGraphQLRequest(GET_VISITOR_QUERY, {
                visitorId: createdId,
            });

            if (
                getResult.findVisitorsPeople &&
                getResult.findVisitorsPeople.length > 0
            ) {
                return getResult.findVisitorsPeople[0];
            }

            // Return at least the ID if we can't get full data
            // Note: ndaSigned defaults to false for new visitors
            return {
                id: createdId,
                firstName,
                surname,
                email,
                phone,
                ndaSigned: false,
                company: null,
            };
        }
        throw new Error("No visitor created");
    } catch (error) {
        console.error("Error creating visitor:", error);
        throw error;
    }
}

/**
 * Create a new visit record in Fibery
 * @param {string} visitorId - The Fibery visitor ID
 * @returns {Promise<object>} Created visit object
 */
async function createVisit(visitorId) {
    try {
        // Get today's date at 00:00 and 23:59
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const visitStart = today.toISOString();

        const visitEnd = new Date(today);
        visitEnd.setHours(23, 59, 59, 999);
        const visitEndISO = visitEnd.toISOString();

        console.log(`Creating visit for visitor ${visitorId}`);
        console.log(`Visit Start: ${visitStart}`);
        console.log(`Visit End: ${visitEndISO}`);

        const createResult = await makeGraphQLRequest(CREATE_VISIT_MUTATION, {
            visitorId,
            visitStart,
            visitEnd: visitEndISO,
        });

        if (
            createResult.visitorsVisits &&
            createResult.visitorsVisits.create &&
            createResult.visitorsVisits.create.entities &&
            createResult.visitorsVisits.create.entities.length > 0
        ) {
            const createdId = createResult.visitorsVisits.create.entities[0].id;
            console.log("Visit created with ID:", createdId);

            // Fetch the full visit data
            const getResult = await makeGraphQLRequest(GET_VISIT_QUERY, {
                visitId: createdId,
            });

            if (
                getResult.findVisitorsVisits &&
                getResult.findVisitorsVisits.length > 0
            ) {
                return getResult.findVisitorsVisits[0];
            }

            // Return at least the ID if we can't get full data
            return { id: createdId, visitStart, visitEnd: visitEndISO };
        }
        throw new Error("No visit created");
    } catch (error) {
        console.error("Error creating visit:", error);
        throw error;
    }
}

/**
 * Find or create a visitor
 * @param {string} firstName - First name
 * @param {string} surname - Surname
 * @param {string} email - Email address
 * @param {string} phone - Phone number (optional)
 * @param {string} uniFiToken - UniFi Access API token (optional)
 * @returns {Promise<object>} Visitor object with visit information
 */
async function findOrCreateVisitor(
    firstName,
    surname,
    email,
    phone = "",
    uniFiToken = "",
) {
    try {
        let fiberyVisitor = null;
        let isNew = false;

        // Fetch the visitor list once and check both email and name against
        // it locally, instead of two separate sequential full-table fetches.
        const lookupResult = await makeGraphQLRequest(FIND_VISITOR_QUERY, {});
        const visitors = lookupResult.findVisitorsPeople;
        if (visitors && Array.isArray(visitors) && visitors.length > 0) {
            if (email) {
                fiberyVisitor = visitors.find((v) => v.email === email) || null;
                if (fiberyVisitor) {
                    console.log("Visitor found by email:", fiberyVisitor.id);
                }
            }

            if (!fiberyVisitor) {
                fiberyVisitor =
                    visitors.find(
                        (v) =>
                            v.firstName === firstName && v.surname === surname,
                    ) || null;
                if (fiberyVisitor) {
                    console.log("Visitor found by name:", fiberyVisitor.id);
                }
            }
        }

        // Create new visitor in Fibery if not found
        if (!fiberyVisitor) {
            console.log("Creating new visitor in Fibery");
            fiberyVisitor = await createVisitor(
                firstName,
                surname,
                email,
                phone,
            );
            isNew = true;
        }

        // Create visit record in Fibery
        const visit = await createVisit(fiberyVisitor.id);

        // Create visitor in UniFi Access if token provided
        let uniFiVisitor = null;
        if (uniFiToken) {
            try {
                console.log("Creating visitor in UniFi Access");
                uniFiVisitor = await createUniFiVisitor(
                    firstName,
                    surname,
                    email,
                    phone,
                    uniFiToken,
                );
                console.log("UniFi visitor created:", uniFiVisitor);
            } catch (uniFiError) {
                console.error(
                    "Warning: Failed to create visitor in UniFi Access:",
                    uniFiError,
                );
                // Don't throw - Fibery creation was successful
                // Just log the warning
            }
        }

        return {
            ...fiberyVisitor,
            isNew,
            visit,
            uniFiVisitor,
        };
    } catch (error) {
        console.error("Error in findOrCreateVisitor:", error);
        throw error;
    }
}

/**
 * Update visitor NDA signed status in Fibery
 * @param {string} visitorId - The Fibery visitor ID
 * @returns {Promise<object>} Updated visitor object
 */
async function updateVisitorNdaSigned(visitorId) {
    try {
        const updateResult = await makeGraphQLRequest(
            UPDATE_NDA_SIGNED_MUTATION,
            {
                visitorId,
                ndaSigned: true,
            },
        );

        if (
            updateResult.visitorsPeople &&
            updateResult.visitorsPeople.update &&
            updateResult.visitorsPeople.update.message
        ) {
            console.log("Visitor NDA signed status updated:", visitorId);
            console.log(
                "Update message:",
                updateResult.visitorsPeople.update.message,
            );

            // The mutation's success message already confirms the write, so
            // skip the extra confirmation round trip and return immediately.
            return { id: visitorId, ndaSigned: true };
        }
        throw new Error("Failed to update NDA signed status");
    } catch (error) {
        console.error("Error updating visitor NDA status:", error);
        throw error;
    }
}

/**
 * Find visitor by first name, surname, and email, then mark NDA as signed
 * @param {string} firstName - First name
 * @param {string} surname - Surname
 * @param {string} email - Email address
 * @returns {Promise<object>} Updated visitor object or null if not found
 */
async function markVisitorNdaSigned(firstName, surname, email) {
    try {
        console.log(`Looking up visitor: ${firstName} ${surname} (${email})`);

        // Fetch all visitors
        const result = await makeGraphQLRequest(FIND_VISITOR_QUERY, {});

        const visitors = result.findVisitorsPeople;
        if (!visitors || !Array.isArray(visitors) || visitors.length === 0) {
            console.log("No visitors found in database");
            return null;
        }

        // Search for matching visitor
        const matchingVisitor = visitors.find(
            (v) =>
                v.firstName === firstName &&
                v.surname === surname &&
                v.email === email,
        );

        if (!matchingVisitor) {
            console.log(
                "Visitor not found with exact match. Available visitors:",
                visitors.map((v) => `${v.firstName} ${v.surname} (${v.email})`),
            );
            return null;
        }

        console.log("Visitor found:", matchingVisitor.id);

        // Update NDA signed status
        const updatedVisitor = await updateVisitorNdaSigned(matchingVisitor.id);

        console.log(
            "NDA signed status updated for visitor:",
            matchingVisitor.id,
        );
        return updatedVisitor;
    } catch (error) {
        console.error("Error marking visitor NDA as signed:", error);
        throw error;
    }
}

export {
    findVisitorByEmail,
    findVisitorByName,
    createVisitor,
    createVisit,
    findOrCreateVisitor,
    updateVisitorNdaSigned,
    markVisitorNdaSigned,
};
